import type { TriggerProps } from '@atlaskit/popup';

import { type DateRangeScale as DateRangeScaleValues } from './common/utils/utils';

export type Appearance = 'default' | 'select';

export type DatePickerValue = {
	start: string;
	end: string;
};

export type IntervalOption = {
	name: string;
	startDate: Date;
	endDate: Date;
};

export type DateRangeScale = (typeof DateRangeScaleValues)[keyof typeof DateRangeScaleValues];

export enum ChangeSource {
	DATE_SELECT = 'DATE_SELECT',
	SCALE_SELECT = 'SCALE_SELECT',
}

export type CalendarProps = {
	date?: string;
	previousDate?: string;
	onSelect: (date?: DatePickerValue) => void;
	dateType?: string;
	selectedDate?: Date | null;
	withTimeScale?: boolean;
	minDate?: Date;
	maxDate?: Date;
};

export type CalendarState = {
	initialYear: number;
	year: number;
	month: number;
};

export type DateRangeSize = 'small' | 'medium';

export type PickerProps = {
	value?: DatePickerValue | undefined;
	previousValue?: DatePickerValue;
	defaultValue?: DatePickerValue | undefined;
	onChange: (
		newValue: Required<DatePickerValue>,
		confidence: string,
		selectedInterval?: IntervalOption,
		source?: ChangeSource,
	) => void;
	withTimeScale?: boolean;
	defaultRangeScale?: DateRangeScale;
	minDate?: Date;
	maxDate?: Date;
	granularities?: DateRangeScale[];
	intervals?: IntervalOption[];
	size?: DateRangeSize;
	/** When true, the interval picker allows selecting a range of intervals (start and end). */
	isRangeSelect?: boolean;
};

export type FuzzyDatePickerProps = {
	value?: DatePickerValue | undefined;
	defaultValue?: DatePickerValue | undefined;
	placeholder?: string;
	onChange: (
		value?: DatePickerValue,
		confidence?: string,
		selectedInterval?: IntervalOption,
	) => void;
	onClose?: () => void;
	withTimeScale?: boolean;
	onClick?: () => void;
	appearance?: Appearance;
	disabled?: boolean;
	minDate?: Date;
	maxDate?: Date;
	granularities?: DateRangeScale[];
	intervals?: IntervalOption[];
	intervalValue?: IntervalOption;
	showPlaceholderWithSubtleText?: boolean;
	// Only applies when appearance is "select".
	selectIconPlacement?: 'start' | 'end';
	size?: DateRangeSize;
	/** When true, the interval picker allows selecting a range of intervals (start and end). */
	isRangeSelect?: boolean;
};

export type ViewOnlyDateRangeProps = {
	value: DatePickerValue | undefined;
	withTimeScale?: boolean;
	placeholder?: string;
	hasStrikethrough?: boolean;
};

export type FuzzyDateProps = Pick<
	FuzzyDatePickerProps,
	'value' | 'placeholder' | 'selectIconPlacement'
> & {
	triggerProps?: TriggerProps;
	onClick?: () => void;
	onClearDate?: () => void;
	withTimeScale?: boolean;
	appearance?: Appearance;
	showIcon?: boolean;
	disabled?: boolean;
	showPlaceholderWithSubtleText?: boolean;
	size?: DateRangeSize;
	/** When set, shown instead of the formatted date range (e.g. interval name). */
	intervalDisplayName?: string;
	forceShowFocusRing?: boolean;
};

export type TargetDate = {
	date?: string | null;
	confidence?: string | null;
};
